/**
 * CartPage - shopping cart + multi-item Stripe checkout.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Plus, Minus, Trash2, Loader2, ArrowLeft, ShoppingCart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const CartPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  const load = useCallback(async () => {
    if (!token) { navigate('/auth'); return; }
    try {
      const res = await axios.get(`${API_URL}/api/store/cart`, { headers: authHeaders() });
      setCart(res.data.cart);
    } catch (e) {
      toast.error('Could not load your cart');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const items = cart?.items || [];

  const removeItem = async (it) => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (it.size) params.append('size', it.size);
      if (it.color) params.append('color', it.color);
      await axios.delete(`${API_URL}/api/store/cart/item/${it.product_id}?${params}`, { headers: authHeaders() });
      await load();
    } catch { toast.error('Could not remove item'); } finally { setBusy(false); }
  };

  const setQty = async (it, qty) => {
    if (qty < 1) return removeItem(it);
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (it.size) params.append('size', it.size);
      if (it.color) params.append('color', it.color);
      // Remove then re-add at the new quantity (no direct update endpoint).
      await axios.delete(`${API_URL}/api/store/cart/item/${it.product_id}?${params}`, { headers: authHeaders() });
      await axios.post(`${API_URL}/api/store/cart/add`,
        { product_id: it.product_id, quantity: qty, size: it.size || null, color: it.color || null },
        { headers: authHeaders() });
      await load();
    } catch { toast.error('Could not update quantity'); } finally { setBusy(false); }
  };

  const clearCart = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/store/cart/clear`, { headers: authHeaders() });
      await load();
    } catch { toast.error('Could not clear cart'); } finally { setBusy(false); }
  };

  const checkout = async () => {
    setCheckingOut(true);
    try {
      const res = await axios.post(`${API_URL}/api/store/checkout`,
        { origin_url: window.location.origin }, { headers: authHeaders() });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Could not start checkout');
        setCheckingOut(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout');
      setCheckingOut(false);
    }
  };

  const total = cart?.total || 0;

  return (
    <div className="min-h-screen bg-[#050505] pb-32">
      <div className="sticky top-0 z-20 bg-[#0B0A10]/90 backdrop-blur-xl border-b border-white/10 px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate('/marketplace')} className="p-2 rounded-full hover:bg-white/10 text-white" data-testid="cart-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded' }}>
          <ShoppingCart className="w-5 h-5 text-[#00F0FF]" /> Your Cart
        </h1>
        {items.length > 0 && (
          <button onClick={clearCart} disabled={busy} className="ml-auto text-xs text-[#71717A] hover:text-white" data-testid="cart-clear">
            Clear all
          </button>
        )}
      </div>

      <div className="px-4 pt-4">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#00F0FF] animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-24" data-testid="cart-empty">
            <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-[#52525B]" />
            <h3 className="text-xl font-semibold text-white mb-2">Your cart is empty</h3>
            <p className="text-[#71717A] mb-6">Add some merch, music or tickets to get started.</p>
            <button onClick={() => navigate('/marketplace')} className="px-6 py-3 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold" data-testid="cart-shop-btn">
              Browse Marketplace
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="cart-items">
            {items.map((it, idx) => {
              const p = it.product || {};
              const img = (p.images && p.images[0]) || null;
              return (
                <motion.div
                  key={`${it.product_id}-${it.size || ''}-${it.color || ''}-${idx}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex gap-3 p-3 bg-[#12101A] rounded-2xl border border-white/5"
                  data-testid={`cart-item-${it.product_id}`}
                >
                  <div className="w-20 h-20 rounded-xl bg-[#1a1625] overflow-hidden shrink-0">
                    {img ? <img src={img} alt={p.name} className="w-full h-full object-cover" /> : (
                      <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-7 h-7 text-[#52525B]" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">{p.name || 'Product'}</p>
                    <p className="text-[#71717A] text-xs truncate">{p.artist_name || ''}</p>
                    {(it.size || it.color) && (
                      <p className="text-[#A1A1AA] text-xs mt-0.5">{[it.size, it.color].filter(Boolean).join(' · ')}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setQty(it, it.quantity - 1)} disabled={busy} className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white disabled:opacity-40" data-testid={`cart-dec-${it.product_id}`}>
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="text-white text-sm w-6 text-center" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                        <button onClick={() => setQty(it, it.quantity + 1)} disabled={busy} className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white disabled:opacity-40" data-testid={`cart-inc-${it.product_id}`}>
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="text-white font-bold">${(Number(p.price || 0) * it.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                  <button onClick={() => removeItem(it)} disabled={busy} className="p-2 h-9 rounded-lg text-red-400 hover:bg-red-500/15 shrink-0" data-testid={`cart-remove-${it.product_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-[#0B0A10]/95 backdrop-blur-xl border-t border-white/10 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[#A1A1AA]">Total</span>
            <span className="text-white text-2xl font-bold" data-testid="cart-total">${total.toFixed(2)}</span>
          </div>
          <button
            onClick={checkout}
            disabled={checkingOut || busy}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="cart-checkout-btn"
          >
            {checkingOut ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShoppingCart className="w-5 h-5" />}
            {checkingOut ? 'Starting checkout…' : 'Checkout'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CartPage;
